import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { teacherService } from '../services/teacherService';
import { schoolService } from '../services/schoolService';
import { classService } from '../services/classService';
import { Teacher, TeacherRequest, School, Class, TeacherSchoolAssignment, AssignmentRequest } from '../types';
import { PlusIcon, SearchIcon, UsersIcon } from '../components/Icons';
import TeacherImportDialog from '../components/TeacherImportDialog';

const TeacherListPage: React.FC = () => {
  const navigate = useNavigate();
  const { schoolId: schoolIdParam } = useParams<{ schoolId: string }>();
  const schoolId = parseInt(schoolIdParam || '0');
  const [school, setSchool] = useState<School | null>(null);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // Form
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null);
  const [formData, setFormData] = useState<TeacherRequest>({
    idCardNumber: '',
    name: '',
    schoolId: 0,
    employeeNumber: '',
    department: '',
    position: '',
    phone: '',
    subjects: '',
    hireDate: '',
    notes: '',
  });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Permission management
  const [showPermModal, setShowPermModal] = useState(false);
  const [permTeacher, setPermTeacher] = useState<Teacher | null>(null);
  const [allClasses, setAllClasses] = useState<Class[]>([]);
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);
  const [isSchoolAdmin, setIsSchoolAdmin] = useState(false);
  const [loadingPerms, setLoadingPerms] = useState(false);

  // Password reset
  const [confirmResetId, setConfirmResetId] = useState<number | null>(null);

  // Delete confirm
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  // Assignment history
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentTeacher, setAssignmentTeacher] = useState<Teacher | null>(null);
  const [assignments, setAssignments] = useState<TeacherSchoolAssignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [showAddAssignment, setShowAddAssignment] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState<AssignmentRequest>({
    schoolId: 0, academicYear: '', department: '', position: '', notes: '',
  });

  useEffect(() => {
    if (schoolId) {
      loadSchool();
      loadTeachers();
    }
  }, [schoolId, page, searchTerm]);

  const loadSchool = async () => {
    try {
      const data = await schoolService.getSchool(schoolId);
      setSchool(data);
    } catch (err) {
      setError('加载学校信息失败');
    }
  };

  const loadTeachers = async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const result = await teacherService.getTeachers({
        schoolId: schoolId,
        search: searchTerm || undefined,
        page,
        size: 20,
      });
      setTeachers(result.content);
      setTotalPages(result.totalPages);
      setTotalElements(result.totalElements);
    } catch (err) {
      setError('加载教师列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTeacher(null);
    setFormData({
      idCardNumber: '',
      name: '',
      schoolId: schoolId,
      employeeNumber: '',
      department: '',
      position: '',
      phone: '',
      subjects: '',
      hireDate: '',
      notes: '',
    });
    setFormError('');
    setShowModal(true);
  };

  const handleEdit = (teacher: Teacher) => {
    setEditingTeacher(teacher);
    setFormData({
      idCardNumber: teacher.idCardNumber,
      name: teacher.name,
      schoolId: teacher.schoolId,
      employeeNumber: teacher.employeeNumber || '',
      department: teacher.department || '',
      position: teacher.position || '',
      phone: teacher.phone || '',
      subjects: teacher.subjects || '',
      hireDate: teacher.hireDate || '',
      notes: teacher.notes || '',
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.idCardNumber || !formData.name) {
      setFormError('请填写必填字段');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      if (editingTeacher) {
        await teacherService.updateTeacher(editingTeacher.id, formData);
      } else {
        await teacherService.createTeacher(formData);
      }
      setShowModal(false);
      loadTeachers();
    } catch (err: any) {
      setFormError(err.response?.data?.message || '操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await teacherService.deleteTeacher(id);
      setConfirmDeleteId(null);
      loadTeachers();
    } catch (err: any) {
      setError(err.response?.data?.message || '删除失败');
    }
  };

  const handleResetPassword = async (id: number) => {
    try {
      await teacherService.resetPassword(id);
      setConfirmResetId(null);
    } catch (err: any) {
      setError(err.response?.data?.message || '密码重置失败');
    }
  };

  const handleManagePermissions = async (teacher: Teacher) => {
    setPermTeacher(teacher);
    setLoadingPerms(true);
    setShowPermModal(true);
    try {
      // Load classes for this school
      const years = await classService.getAcademicYears(schoolId);
      let classes: Class[] = [];
      for (const year of years) {
        const result = await classService.getClasses({
          schoolId: schoolId,
          year,
          size: 200,
        });
        classes = classes.concat(result.content);
      }
      setAllClasses(classes);

      // Load existing permissions
      const perms = await teacherService.getPermissions(teacher.id);
      setIsSchoolAdmin(perms.isSchoolAdmin);
      setSelectedClassIds(perms.classIds || []);
    } catch (err) {
      setError('加载权限信息失败');
    } finally {
      setLoadingPerms(false);
    }
  };

  const handleSavePermissions = async () => {
    if (!permTeacher) return;
    setLoadingPerms(true);
    try {
      await teacherService.updatePermissions(permTeacher.id, {
        isSchoolAdmin,
        classIds: isSchoolAdmin ? [] : selectedClassIds,
      });
      setShowPermModal(false);
      loadTeachers(); // reload to update permission badges
    } catch (err: any) {
      setError(err.response?.data?.message || '保存权限失败');
    } finally {
      setLoadingPerms(false);
    }
  };

  const toggleClassPermission = (classId: string) => {
    setSelectedClassIds(prev =>
      prev.includes(classId)
        ? prev.filter(id => id !== classId)
        : [...prev, classId]
    );
  };

  const handleViewAssignments = async (teacher: Teacher) => {
    setAssignmentTeacher(teacher);
    setShowAssignmentModal(true);
    setShowAddAssignment(false);
    setLoadingAssignments(true);
    try {
      const data = await teacherService.getAssignments(teacher.id);
      setAssignments(data);
    } catch (err: any) {
      alert(err.response?.data?.message || '获取任职历史失败');
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleAddAssignment = async () => {
    if (!assignmentTeacher || !assignmentForm.schoolId || !assignmentForm.academicYear) return;
    try {
      await teacherService.addAssignment(assignmentTeacher.id, assignmentForm);
      setShowAddAssignment(false);
      setAssignmentForm({ schoolId: 0, academicYear: '', department: '', position: '', notes: '' });
      const data = await teacherService.getAssignments(assignmentTeacher.id);
      setAssignments(data);
      loadTeachers();
    } catch (err: any) {
      alert(err.response?.data?.message || '添加任职记录失败');
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/schools')}
          className="text-sm text-on-surface-variant hover:text-on-surface mb-4 flex items-center space-x-1"
        >
          <span>←</span>
          <span>返回学校列表</span>
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-on-surface font-headline">
              {school?.name} - 教师管理
            </h1>
            <p className="text-sm text-on-surface-variant mt-1 font-body">管理教师账户、权限分配</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                placeholder="搜索姓名或工号..."
                className="pl-10 pr-4 py-2.5 bg-surface-container-high rounded-xl text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none w-64"
              />
            </div>
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center px-4 py-2.5 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low text-sm font-medium"
            >
              <span className="material-symbols-outlined mr-1" style={{ fontSize: '18px' }}>upload_file</span>
              批量导入
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center px-4 py-2.5 gradient-primary text-on-primary rounded-xl hover:opacity-90 text-sm font-medium shadow-teal"
            >
              <PlusIcon size={18} className="mr-1" />
              添加教师
            </button>
          </div>
        </div>
      </div>

      <TeacherImportDialog
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          setShowImportModal(false);
          loadTeachers();
        }}
        schoolId={schoolId}
      />

      {error && (
        <div className="mb-4 p-3 bg-red-50 rounded-xl text-red-700 text-sm shadow-soft">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-surface-container-lowest rounded-3xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-on-surface-variant font-body">加载中...</div>
        ) : teachers.length === 0 ? (
          <div className="p-8 text-center text-on-surface-variant font-body">暂无教师数据</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-container-low" style={{ borderBottom: '1px solid rgba(171, 179, 183, 0.2)' }}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase font-label">姓名</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase font-label">身份证号(登录账号)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase font-label">工号</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase font-label">部门</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase font-label">职位</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase font-label">电话</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase font-label">状态</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase font-label">权限</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-on-surface-variant uppercase font-label">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-container-low">
                  {teachers.map(teacher => (
                    <tr key={teacher.id} className="hover:bg-surface-container-low">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-on-surface">{teacher.name}</div>
                        <div className="text-xs text-on-surface-variant font-label">{teacher.genderDisplay}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-on-surface font-mono">{teacher.idCardNumber}</td>
                      <td className="px-4 py-3 text-sm text-on-surface font-body">{teacher.employeeNumber || '-'}</td>
                      <td className="px-4 py-3 text-sm text-on-surface font-body">{teacher.department || '-'}</td>
                      <td className="px-4 py-3 text-sm text-on-surface font-body">{teacher.position || '-'}</td>
                      <td className="px-4 py-3 text-sm text-on-surface font-body">{teacher.phone || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          teacher.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                          teacher.status === 'RETIRED' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {teacher.statusDisplay}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {teacher.isSchoolAdmin ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            全校
                          </span>
                        ) : teacher.permissionCount && teacher.permissionCount > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-container-high text-on-surface font-label">
                            {teacher.permissionCount}个班级
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button onClick={() => handleEdit(teacher)} className="text-sm text-primary-600 hover:text-primary-800">编辑</button>
                          <button onClick={() => handleViewAssignments(teacher)} className="text-sm text-teal-600 hover:text-teal-800">任职历史</button>
                          <button onClick={() => handleManagePermissions(teacher)} className="text-sm text-indigo-600 hover:text-indigo-800">权限</button>
                          <button onClick={() => setConfirmResetId(teacher.id)} className="text-sm text-amber-600 hover:text-amber-800">重置密码</button>
                          <button onClick={() => setConfirmDeleteId(teacher.id)} className="text-sm text-red-600 hover:text-red-800">删除</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: '1px solid rgba(171, 179, 183, 0.2)' }}>
                <div className="text-sm text-on-surface font-label">
                  共 {totalElements} 位教师
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="px-3 py-1 bg-surface-container-high rounded-xl text-sm disabled:opacity-50 hover:bg-surface-container-low"
                  >
                    上一页
                  </button>
                  <span className="px-3 py-1 text-sm text-on-surface font-label">
                    {page + 1} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                    className="px-3 py-1 bg-surface-container-high rounded-xl text-sm disabled:opacity-50 hover:bg-surface-container-low"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface-container-lowest rounded-3xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6" style={{ borderBottom: '1px solid rgba(171, 179, 183, 0.2)' }}>
              <h2 className="text-lg font-semibold text-on-surface font-headline">
                {editingTeacher ? '编辑教师' : '添加教师'}
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {formError && (
                <div className="p-3 bg-red-50 rounded-xl text-red-700 text-sm shadow-soft">{formError}</div>
              )}

              {!editingTeacher && (
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-1 font-label">身份证号 *</label>
                  <input
                    type="text"
                    value={formData.idCardNumber}
                    onChange={(e) => setFormData({ ...formData, idCardNumber: e.target.value })}
                    className="w-full bg-surface-container-high rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none"
                    maxLength={18}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-on-surface mb-1 font-label">姓名 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-surface-container-high rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-1 font-label">工号</label>
                  <input
                    type="text"
                    value={formData.employeeNumber}
                    onChange={(e) => setFormData({ ...formData, employeeNumber: e.target.value })}
                    className="w-full bg-surface-container-high rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-1 font-label">电话</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full bg-surface-container-high rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-1 font-label">部门</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full bg-surface-container-high rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-1 font-label">职位</label>
                  <input
                    type="text"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="w-full bg-surface-container-high rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface mb-1 font-label">入职日期</label>
                <input
                  type="date"
                  value={formData.hireDate}
                  onChange={(e) => setFormData({ ...formData, hireDate: e.target.value })}
                  className="w-full bg-surface-container-high rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface mb-1 font-label">备注</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full bg-surface-container-high rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none"
                  rows={2}
                />
              </div>
            </div>
            <div className="p-6 flex justify-end space-x-3" style={{ borderTop: '1px solid rgba(171, 179, 183, 0.2)' }}>
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-surface-container-high rounded-xl text-sm text-on-surface hover:bg-surface-container-low"
              >
                取消
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-4 py-2 gradient-primary text-on-primary rounded-xl text-sm hover:opacity-90 disabled:opacity-50 shadow-teal"
              >
                {submitting ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permission Modal */}
      {showPermModal && permTeacher && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface-container-lowest rounded-3xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="p-6" style={{ borderBottom: '1px solid rgba(171, 179, 183, 0.2)' }}>
              <h2 className="text-lg font-semibold text-on-surface font-headline">
                权限管理 - {permTeacher.name}
              </h2>
              <p className="text-sm text-on-surface-variant mt-1 font-body">设置该教师的数据查看权限</p>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {loadingPerms ? (
                <div className="text-center text-on-surface-variant py-4 font-body">加载中...</div>
              ) : (
                <div className="space-y-4">
                  {/* Permission type radio buttons */}
                  <div className="space-y-2">
                    <label
                      className={`flex items-center p-3 rounded-2xl cursor-pointer transition-colors ${
                        isSchoolAdmin ? 'bg-blue-50 shadow-soft' : 'bg-surface-container-low hover:bg-surface-container-high'
                      }`}
                    >
                      <input
                        type="radio"
                        name="permType"
                        checked={isSchoolAdmin}
                        onChange={() => setIsSchoolAdmin(true)}
                        className="mr-3 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <div className="text-sm font-medium text-on-surface">全校管理员</div>
                        <div className="text-xs text-on-surface-variant font-label">可查看全校所有班级的数据</div>
                      </div>
                    </label>
                    <label
                      className={`flex items-center p-3 rounded-2xl cursor-pointer transition-colors ${
                        !isSchoolAdmin ? 'bg-primary-50 shadow-soft' : 'bg-surface-container-low hover:bg-surface-container-high'
                      }`}
                    >
                      <input
                        type="radio"
                        name="permType"
                        checked={!isSchoolAdmin}
                        onChange={() => setIsSchoolAdmin(false)}
                        className="mr-3 text-primary-600 focus:ring-primary-500"
                      />
                      <div>
                        <div className="text-sm font-medium text-on-surface">班级管理员</div>
                        <div className="text-xs text-on-surface-variant font-label">仅查看已分配班级的数据</div>
                      </div>
                    </label>
                  </div>

                  {/* Class list (only shown for class admin) */}
                  {!isSchoolAdmin && (
                    <>
                      <div className="pt-4" style={{ borderTop: '1px solid rgba(171, 179, 183, 0.2)' }}>
                        <h3 className="text-sm font-medium text-on-surface mb-3 font-label">选择班级</h3>
                        {allClasses.length === 0 ? (
                          <div className="text-center text-on-surface-variant py-4 font-body">该学校暂无班级</div>
                        ) : (
                          <div className="space-y-4">
                            {/* Group by academicYear, then by grade */}
                            {Object.entries(
                              allClasses.reduce<Record<string, Record<string, Class[]>>>((acc, cls) => {
                                const year = cls.academicYear || '未分配学年';
                                const grade = cls.grade || '其他';
                                if (!acc[year]) acc[year] = {};
                                if (!acc[year][grade]) acc[year][grade] = [];
                                acc[year][grade].push(cls);
                                return acc;
                              }, {})
                            )
                              .sort(([a], [b]) => b.localeCompare(a))
                              .map(([year, grades]) => (
                                <div key={year}>
                                  <h4 className="text-xs font-semibold text-on-surface-variant uppercase mb-2 font-label">{year}</h4>
                                  {Object.entries(grades).map(([grade, classes]) => (
                                    <div key={grade} className="mb-3">
                                      <h5 className="text-sm font-medium text-on-surface-variant mb-1.5 ml-1 font-label">{grade}</h5>
                                      <div className="grid grid-cols-2 gap-2">
                                        {classes.map(cls => (
                                          <label
                                            key={cls.classId}
                                            className={`flex items-center p-2 rounded-xl cursor-pointer transition-colors ${
                                              selectedClassIds.includes(cls.classId)
                                                ? 'bg-primary-50 shadow-soft'
                                                : 'bg-surface-container-low hover:bg-surface-container-high'
                                            }`}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={selectedClassIds.includes(cls.classId)}
                                              onChange={() => toggleClassPermission(cls.classId)}
                                              className="mr-2 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                            />
                                            <span className="text-sm text-on-surface font-body">{cls.name}</span>
                                          </label>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="p-6 flex justify-between items-center" style={{ borderTop: '1px solid rgba(171, 179, 183, 0.2)' }}>
              <span className="text-sm text-on-surface-variant font-label">
                {isSchoolAdmin ? '全校管理员' : `已选择 ${selectedClassIds.length} 个班级`}
              </span>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowPermModal(false)}
                  className="px-4 py-2 bg-surface-container-high rounded-xl text-sm text-on-surface hover:bg-surface-container-low"
                >
                  取消
                </button>
                <button
                  onClick={handleSavePermissions}
                  disabled={loadingPerms}
                  className="px-4 py-2 gradient-primary text-on-primary rounded-xl text-sm hover:opacity-90 disabled:opacity-50 shadow-teal"
                >
                  {loadingPerms ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment History Modal */}
      {showAssignmentModal && assignmentTeacher && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface-container-lowest rounded-3xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
            <div className="p-6 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(171, 179, 183, 0.2)' }}>
              <div>
                <h2 className="text-lg font-semibold text-on-surface font-headline">
                  任职历史 - {assignmentTeacher.name}
                </h2>
                <p className="text-sm text-on-surface-variant mt-1 font-body">查看和管理教师的学校任职记录</p>
              </div>
              <button
                onClick={() => {
                  setShowAddAssignment(true);
                  setAssignmentForm({ schoolId: schoolId, academicYear: '', department: '', position: '', notes: '' });
                }}
                className="px-3 py-1.5 gradient-primary text-on-primary rounded-xl hover:opacity-90 text-sm font-medium shadow-teal"
              >
                + 新增记录
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {showAddAssignment && (
                <div className="mb-6 p-4 bg-surface-container-low rounded-2xl shadow-soft">
                  <h3 className="text-sm font-medium text-on-surface mb-3 font-label">新增任职记录</h3>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-on-surface-variant mb-1 font-label">学校</label>
                      <input
                        type="text"
                        value={school?.name || ''}
                        disabled
                        className="w-full px-3 py-2 bg-surface-container-high rounded-xl text-sm opacity-60"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-on-surface-variant mb-1 font-label">学年 *</label>
                      <input
                        type="text"
                        value={assignmentForm.academicYear}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, academicYear: e.target.value })}
                        placeholder="如：2024"
                        className="w-full px-3 py-2 bg-surface-container-high rounded-xl text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-on-surface-variant mb-1 font-label">部门</label>
                      <input
                        type="text"
                        value={assignmentForm.department || ''}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, department: e.target.value })}
                        className="w-full px-3 py-2 bg-surface-container-high rounded-xl text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-on-surface-variant mb-1 font-label">职位</label>
                      <input
                        type="text"
                        value={assignmentForm.position || ''}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, position: e.target.value })}
                        className="w-full px-3 py-2 bg-surface-container-high rounded-xl text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="block text-xs text-on-surface-variant mb-1 font-label">备注</label>
                    <input
                      type="text"
                      value={assignmentForm.notes || ''}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, notes: e.target.value })}
                      placeholder="可选"
                      className="w-full px-3 py-2 bg-surface-container-high rounded-xl text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none"
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => setShowAddAssignment(false)}
                      className="px-3 py-1.5 bg-surface-container-high rounded-xl text-sm hover:bg-surface-container-low"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleAddAssignment}
                      disabled={!assignmentForm.academicYear}
                      className="px-3 py-1.5 gradient-primary text-on-primary rounded-xl text-sm hover:opacity-90 disabled:opacity-50 shadow-teal"
                    >
                      添加
                    </button>
                  </div>
                </div>
              )}

              {loadingAssignments ? (
                <div className="text-center text-on-surface-variant py-8 font-body">加载中...</div>
              ) : assignments.length === 0 ? (
                <div className="text-center text-on-surface-variant py-8 font-body">暂无任职记录</div>
              ) : (
                <div className="space-y-3">
                  {assignments.map((assignment) => (
                    <div
                      key={assignment.id}
                      className={`p-4 rounded-2xl ${
                        assignment.isCurrent
                          ? 'bg-primary-50 shadow-soft'
                          : 'bg-surface-container-lowest shadow-soft'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-on-surface">
                              {assignment.schoolName}
                            </span>
                            {assignment.isCurrent && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                                当前
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-on-surface-variant mt-1 font-label">
                            学年: {assignment.academicYear}
                            {assignment.department && ` | 部门: ${assignment.department}`}
                            {assignment.position && ` | 职位: ${assignment.position}`}
                          </div>
                          {assignment.notes && (
                            <div className="text-xs text-on-surface-variant mt-1 font-label">{assignment.notes}</div>
                          )}
                        </div>
                        <div className="text-xs text-on-surface-variant font-label">
                          {new Date(assignment.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6 flex justify-end" style={{ borderTop: '1px solid rgba(171, 179, 183, 0.2)' }}>
              <button
                onClick={() => setShowAssignmentModal(false)}
                className="px-4 py-2 bg-surface-container-high rounded-xl text-sm text-on-surface hover:bg-surface-container-low"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password confirm */}
      {confirmResetId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface-container-lowest rounded-3xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold text-on-surface mb-2 font-headline">确认重置密码</h3>
            <p className="text-sm text-on-surface-variant mb-4 font-body">密码将重置为身份证后6位，确认继续？</p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setConfirmResetId(null)} className="px-4 py-2 bg-surface-container-high rounded-xl text-sm hover:bg-surface-container-low">取消</button>
              <button onClick={() => handleResetPassword(confirmResetId)} className="px-4 py-2 bg-amber-600 text-white rounded-xl text-sm hover:bg-amber-700">确认重置</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface-container-lowest rounded-3xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold text-on-surface mb-2 font-headline">确认删除</h3>
            <p className="text-sm text-on-surface-variant mb-4 font-body">教师将被标记为离职状态，确认继续？</p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setConfirmDeleteId(null)} className="px-4 py-2 bg-surface-container-high rounded-xl text-sm hover:bg-surface-container-low">取消</button>
              <button onClick={() => handleDelete(confirmDeleteId)} className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm hover:bg-red-700">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherListPage;
